import React from 'react'

import { SIDEBAR_MINIMIZED_WIDTH, SIDEBAR_WIDTH } from 'config'
import { LockIcon, LockOpenIcon, PanelLeftCloseIcon, PanelLeftOpenIcon } from 'lucide-react'
import { useShortcutBindings } from 'hooks'
import { ariaBinding, formatBinding } from 'utils'

import { Box, IconButton, ListItem, ListItemButton, ListItemIcon, ListItemText, Tooltip } from '@mui/material'
import { useTheme } from '@mui/material/styles'

import { KeyChip, KEY_CHIP_HEIGHT } from 'components/display'

interface Props {
  sidebarOpen?: boolean
  onToggle: () => void
  /** True when the sidebar follows the pointer rather than staying where it is put. */
  isAuto: boolean
  onToggleAuto: () => void
}

/**
 * Collapse / expand the sidebar. Sits at the TOP OF THE NAV — first row under the
 * logo, above Search — not in the brand row beside the logo, where at 80px it
 * crowds the mark.
 *
 * Built from `SidebarList`'s row shape for the same reason `SidebarSearchTrigger`
 * is: the icon then aligns with every nav icon below it by construction rather
 * than by hand-tuned padding.
 *
 * Renders in BOTH states — `PanelLeftClose` expanded, `PanelLeftOpen` collapsed —
 * so a collapsed sidebar is never a dead end. Same two icons Vergence uses.
 *
 * Deliberately a TWO-state toggle plus a separate lock, NOT a three-state cycle.
 * `auto` and `collapsed` are pixel-identical at rest (auto only differs once the
 * pointer is over the sidebar), so a cycle would have two stops a user cannot tell
 * apart: click, see nothing change, click again. Splitting it into "which state"
 * (this row) and "can the pointer override it" (the lock) keeps every control's
 * effect visible at the moment it is used.
 *
 * All three writers — this row, the lock, and the profile menu — set the same
 * stored mode, so they cannot disagree.
 */
export const SidebarCollapseToggle = ({ sidebarOpen, onToggle, isAuto, onToggleAuto }: Props) => {
  const theme = useTheme()
  // Read from the catalog rather than a local constant: this row ADVERTISES the
  // binding that `Sidebar` LISTENS for, and the two must be one value or the hint
  // starts lying the moment the user rebinds it.
  const { sidebarToggle } = useShortcutBindings()
  const Icon = sidebarOpen ? PanelLeftCloseIcon : PanelLeftOpenIcon

  // pt:4 (16px), not the nav rows' pt:1 — this is the first row under the header
  // divider and needs to sit clear of it rather than tucked against it. pb:0
  // because the Search row below contributes its own top padding.
  return (
    <ListItem sx={{ pt: 4, pb: 0, pr: 3, pl: 3, textDecoration: 'none' }}>
      <ListItemButton
        aria-label={sidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}
        aria-keyshortcuts={ariaBinding(sidebarToggle)}
        // This row OPERATES the rail rather than navigating anywhere, so it opts out
        // of the per-icon hover motion every nav row gets. A greeting animation needs
        // a thing to be about, and there is no destination here for it to express.
        // See ../../iconMotion.ts — the marker suppresses motion for the WHOLE row,
        // which matters because this one holds two glyphs (the panel icon and the
        // lock button below), not one.
        data-nav-control=""
        disableRipple
        onClick={onToggle}
        sx={[
          {
            borderRadius: 2,
            pt: 3,
            pb: 3,
            // pr:1, not the nav rows' pr:4. These two rows are the only ones with a
            // TRAILING element; at pr:4 the chip sat 28px off the edge, mirroring the
            // 28px icon inset on the left but crowding the label. Symmetry is not the
            // goal when only one side carries content — the chip moves right and the
            // label gets the room.
            pr: 1,
            // Pinned to the nav rows' 48px. These two rows carry a KeyChip (and the
            // Collapse row a lock button) which are taller than the 24px icon, so
            // without this the row grows only when EXPANDED — shifting every nav row
            // below it down the moment the sidebar opens. Height must not depend on
            // what the row happens to contain.
            height: 48,
          },
          sidebarOpen
            ? { minWidth: SIDEBAR_WIDTH - 24, justifyContent: 'initial' }
            : { maxWidth: SIDEBAR_MINIMIZED_WIDTH - 24 },
        ]}
      >
        <ListItemIcon
          sx={[
            { color: theme.palette.primary[400], minWidth: 0, justifyContent: 'center' },
            sidebarOpen ? { mr: 0 } : { mr: 'auto' },
          ]}
        >
          <Icon strokeWidth={1} />
        </ListItemIcon>
        <ListItemText
          disableTypography
          sx={[
            {
              fontSize: 16,
              fontFamily: 'Lato',
              display: 'flex',
              justifyContent: 'space-between',
              width: '100%',
              alignItems: 'center',
              marginLeft: 3,
              // Nav rows get line-height 1 from their <Link>; this row has none and
              // would otherwise render 6px taller than the column beneath it.
              lineHeight: 1,
              // ListItemText ships 4px top/bottom margins. With the lock button
              // (20px) inside, that made this block 28px — taller than the 24px
              // icon — so the row grew 48->52 when expanded and pushed every nav
              // row down. Zeroed: content now tops out at the icon, 48px in both
              // states.
              my: 0,
              color: theme.palette.primary[400],
            },
            sidebarOpen ? { opacity: 1, display: 'flex' } : { opacity: 0, display: 'none' },
          ]}
        >
          Collapse
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {/* Auto lock. Two controls, two questions: this row's main action sets
                WHICH state the sidebar is in; the lock sets whether the pointer is
                allowed to override it. Unlocked = auto (the default) = follows the
                pointer. Locked = stays where you put it.

                A padlock reads wrong for "pin open" but right for "auto vs fixed",
                which is what it now means.

                Tooltips are not decoration here: two adjacent icons do not explain
                their own relationship, so each says what it does in words.

                stopPropagation because this sits inside the row's button — without
                it, locking would also toggle the sidebar. */}
            <Tooltip
              title={isAuto ? 'Sidebar follows your pointer. Click to keep it fixed.' : 'Sidebar stays fixed. Click to let it follow your pointer.'}
              placement="right"
              arrow
            >
              <IconButton
                aria-label={isAuto ? 'Keep sidebar fixed' : 'Let sidebar follow the pointer'}
                aria-pressed={!isAuto}
                onClick={event => {
                  event.stopPropagation()
                  onToggleAuto()
                }}
                sx={{
                  p: 0,
                  // Squared, not the IconButton default circle: at 16px beside text a
                  // round hover target reads as a stray bubble rather than part of the row.
                  borderRadius: 1,
                  // Sized to the KeyChip it sits next to, from the chip's own exported
                  // height — two adjacent controls whose hover boxes differ by a few px
                  // read as a mistake. Derived, not eyeballed, so it tracks the chip.
                  width: KEY_CHIP_HEIGHT,
                  height: KEY_CHIP_HEIGHT,
                  color: theme.palette.primary[400],
                }}
              >
                {isAuto
                  ? <LockOpenIcon strokeWidth={1} size={16} />
                  : <LockIcon strokeWidth={1} size={16} />}
              </IconButton>
            </Tooltip>
            {/* The SHARED chip, same as the Help popover and the shortcuts dialog —
                a bespoke faint span here is how one surface quietly drifts. */}
            <KeyChip muted>{formatBinding(sidebarToggle)}</KeyChip>
          </Box>
        </ListItemText>
      </ListItemButton>
    </ListItem>
  )
}
